import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const GameContext = createContext();

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export const useGame = () => {
  const context = useContext(GameContext);
  if (!context) {
    throw new Error('useGame must be used within GameProvider');
  }
  return context;
};

export const GameProvider = ({ children }) => {
  const [selectedAge, setSelectedAge] = useState(null);
  const [selectedSubject, setSelectedSubject] = useState(null);
  const [selectedFormat, setSelectedFormat] = useState(null);
  const [playerName, setPlayerName] = useState(localStorage.getItem('playerName') || '');
  const [totalScore, setTotalScore] = useState(parseInt(localStorage.getItem('totalScore')) || 0);
  const [gamesPlayed, setGamesPlayed] = useState(parseInt(localStorage.getItem('gamesPlayed')) || 0);
  const [achievements, setAchievements] = useState(JSON.parse(localStorage.getItem('achievements') || '[]'));
  const [currentGameScore, setCurrentGameScore] = useState(0);
  const [savedProgress, setSavedProgress] = useState(null);
  const [gameProgress, setGameProgress] = useState(null); // {currentQuestion, currentQuiz, score}

  useEffect(() => {
    localStorage.setItem('playerName', playerName);
  }, [playerName]);

  useEffect(() => {
    localStorage.setItem('totalScore', totalScore);
  }, [totalScore]);

  useEffect(() => {
    localStorage.setItem('gamesPlayed', gamesPlayed);
  }, [gamesPlayed]);

  useEffect(() => {
    localStorage.setItem('achievements', JSON.stringify(achievements));
  }, [achievements]);

  // Load saved progress from localStorage on mount
  useEffect(() => {
    const saved = localStorage.getItem('savedProgress');
    if (saved) {
      try {
        setSavedProgress(JSON.parse(saved));
      } catch (e) {
        console.error('Error loading saved progress:', e);
      }
    }
  }, []);

  // Save progress to backend
  const saveProgress = useCallback(async (progress) => {
    if (!playerName) return;
    
    const progressData = {
      ageGroup: selectedAge,
      subject: selectedSubject,
      gameFormat: selectedFormat,
      gameProgress: progress || gameProgress,
      savedAt: new Date().toISOString()
    };
    
    // Save to localStorage
    localStorage.setItem('savedProgress', JSON.stringify(progressData));
    setSavedProgress(progressData);
    
    // Save to backend
    try {
      await fetch(`${API_URL}/api/kids/save-progress`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          player_name: playerName,
          progress: progressData
        })
      });
      console.log('Progress saved to backend');
    } catch (error) {
      console.error('Error saving progress to backend:', error);
    }
  }, [playerName, selectedAge, selectedSubject, selectedFormat, gameProgress]);

  // Load progress from backend
  const loadProgress = useCallback(async () => {
    if (!playerName) return null;
    
    try {
      const response = await fetch(`${API_URL}/api/kids/${encodeURIComponent(playerName)}/progress`);
      if (response.ok) {
        const data = await response.json();
        if (data.has_progress && data.progress) {
          setSavedProgress(data.progress);
          localStorage.setItem('savedProgress', JSON.stringify(data.progress));
          return data.progress;
        }
      }
    } catch (error) {
      console.error('Error loading progress:', error);
    }
    return null;
  }, [playerName]);

  // Clear saved progress
  const clearProgress = useCallback(async () => {
    setSavedProgress(null);
    setGameProgress(null);
    localStorage.removeItem('savedProgress');
    
    if (playerName) {
      try {
        await fetch(`${API_URL}/api/kids/${encodeURIComponent(playerName)}/progress`, {
          method: 'DELETE'
        });
      } catch (error) {
        console.error('Error clearing progress:', error);
      }
    }
  }, [playerName]);

  // Restore progress (set selections from saved progress)
  const restoreProgress = useCallback((progress) => {
    if (progress) {
      setSelectedAge(progress.ageGroup);
      setSelectedSubject(progress.subject);
      setSelectedFormat(progress.gameFormat);
      setGameProgress(progress.gameProgress);
    }
  }, []);

  const addScore = (points) => {
    setCurrentGameScore(prev => prev + points);
    setTotalScore(prev => prev + points);
  };

  const completeGame = () => {
    setGamesPlayed(prev => prev + 1);
    setCurrentGameScore(0);
    clearProgress(); // Clear progress when game is completed
  };

  const addAchievement = (achievement) => {
    if (!achievements.find(a => a.id === achievement.id)) {
      setAchievements(prev => [...prev, achievement]);
    }
  };

  const resetGame = () => {
    setCurrentGameScore(0);
  };

  return (
    <GameContext.Provider
      value={{
        selectedAge,
        setSelectedAge,
        selectedSubject,
        setSelectedSubject,
        selectedFormat,
        setSelectedFormat,
        playerName,
        setPlayerName,
        totalScore,
        gamesPlayed,
        achievements,
        currentGameScore,
        savedProgress,
        gameProgress,
        setGameProgress,
        addScore,
        completeGame,
        addAchievement,
        resetGame,
        saveProgress,
        loadProgress,
        clearProgress,
        restoreProgress,
      }}
    >
      {children}
    </GameContext.Provider>
  );
};
